import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WifiOff } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { RollCallStatus, SrLiveMessage } from '@cloudsign/shared';
import { ApiClientError, api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { Alert, Badge, Button, Card, ConfirmButton, EmptyState, PageHeader, Spinner, TextField } from '../../components/ui';
import { useBuildingLive } from './live';
import { BASE, SrEvacuationHeader, SrEvacuationView, SrMarkResult, StartAnswer, keys, useBuildingZone } from './api';
import type { SrRollCallRow, SrTotals } from './api';

type Filter = 'all' | RollCallStatus;
const FILTERS: Filter[] = ['unaccounted', 'safe', 'off_site', 'missing', 'all'];

const FLAG: Record<RollCallStatus, 'good' | 'warn' | 'bad' | 'neutral'> = { unaccounted: 'warn', safe: 'good', missing: 'bad', off_site: 'neutral' };

export function totalsOf(rows: readonly SrRollCallRow[]): SrTotals {
  const n = { unaccounted: 0, safe: 0, missing: 0, offSite: 0 };
  for (const r of rows) {
    if (r.status === 'unaccounted') n.unaccounted += 1;
    else if (r.status === 'safe') n.safe += 1;
    else if (r.status === 'missing') n.missing += 1;
    else n.offSite += 1;
  }
  return n;
}

/** Replaces or adds one row (live messages and our own marks are idempotent upserts) and keeps the totals in step. */
export function upsertRow(view: SrEvacuationView, row: SrRollCallRow): SrEvacuationView {
  const rows = view.rows.some((r) => r.id === row.id) ? view.rows.map((r) => (r.id === row.id ? row : r)) : [...view.rows, row];
  return { ...view, rows, totals: totalsOf(rows) };
}

/** The session ended (idle limit, signed out elsewhere). This page stays on screen with a banner instead of being sent to the sign-in page. */
export const isSignedOut = (e: unknown): boolean => e instanceof ApiClientError && e.status === 401;

// quiet401: the global handler would clear the page and go to the sign-in page; this page keeps the last list and says so instead.
async function fetchActive(): Promise<SrEvacuationView | null> {
  try {
    return await api.get(`${BASE}/evacuations/active`, { schema: SrEvacuationView, quiet401: true });
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 404) return null;
    throw e;
  }
}

/**
 * The building's live roll call, built for a phone: big buttons, minimal fields (name, company, group, time in, status),
 * live over the building socket, and the last list stays on screen when the connection drops. Building administrators and
 * marshals can both start, mark and end (the server checks the role again).
 */
export function EvacuationPage() {
  const zone = useBuildingZone();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [signedOut, setSignedOut] = useState(false);
  const q = useQuery({ queryKey: keys.active, queryFn: fetchActive, refetchInterval: signedOut ? false : 60_000 });
  useEffect(() => {
    if (isSignedOut(q.error)) setSignedOut(true);
  }, [q.error]);
  const [lastLive, setLastLive] = useState(0);
  const [everConnected, setEverConnected] = useState(false);

  const onMessage = useCallback(
    (m: SrLiveMessage) => {
      setLastLive(Date.now());
      if (m.type === 'sr.evac.row') {
        qc.setQueryData<SrEvacuationView | null>(keys.active, (cur) => (cur ? upsertRow(cur, m.row) : cur));
      } else if (m.type === 'sr.evac.state' || m.type === 'refresh' || m.type === 'members.changed') {
        void qc.invalidateQueries({ queryKey: keys.active });
        if (m.type === 'sr.evac.state') void qc.invalidateQueries({ queryKey: keys.history });
      }
    },
    [qc],
  );
  const { connected } = useBuildingLive(onMessage, () => void qc.invalidateQueries({ queryKey: keys.active }));
  useEffect(() => {
    if (connected) setEverConnected(true);
  }, [connected]);

  const offline = !signedOut && ((everConnected && !connected) || q.isError);
  const updatedAt = Math.max(q.dataUpdatedAt, lastLive);

  return (
    <>
      <PageHeader title={t('srb.evac.title')} description={t('srb.evac.desc')} />
      {signedOut ? (
        <div className="mb-4">
          <Alert kind="error">
            <p className="font-semibold">{t('sr.evac.signedOut')}</p>
            <p className="mt-1">{t('sr.evac.signedOut.detail')}</p>
            <Button className="mt-3" variant="primary" onClick={() => void navigate(`/login?next=${encodeURIComponent('/building/evacuation')}`)}>
              {t('sr.evac.signedOut.button')}
            </Button>
          </Alert>
        </div>
      ) : null}
      {offline && q.data !== undefined ? (
        <div className="mb-4" role="status">
          <Alert kind="info">
            <span className="inline-flex items-center gap-2">
              <WifiOff aria-hidden="true" className="h-4 w-4 shrink-0" />
              {t('srb.evac.offline', { time: updatedAt ? formatDateTime(new Date(updatedAt).toISOString(), zone) : '' })}
            </span>
          </Alert>
        </div>
      ) : null}
      {q.isPending ? (
        <Spinner />
      ) : q.data === undefined ? (
        signedOut ? null : <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data === null ? (
        <StartCard onSignedOut={() => setSignedOut(true)} />
      ) : (
        <ActiveList view={q.data} zone={zone} onSignedOut={() => setSignedOut(true)} />
      )}
    </>
  );
}

function StartCard({ onSignedOut }: { onSignedOut: () => void }) {
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: (kind: 'real' | 'drill') => api.post(`${BASE}/evacuations`, { body: { kind }, schema: StartAnswer, quiet401: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.active }),
    onError: (e) => {
      if (isSignedOut(e)) onSignedOut();
    },
  });
  return (
    <Card title={t('srb.evac.none')} description={t('srb.evac.noneDesc')}>
      <ErrorLine error={start.error} />
      <div className="flex flex-col gap-3 sm:flex-row">
        <ConfirmButton label={t('srb.evac.startReal')} confirmLabel={t('srb.evac.startRealConfirm')} pending={start.isPending} onConfirm={() => start.mutate('real')} />
        <ConfirmButton label={t('srb.evac.startDrill')} confirmLabel={t('srb.evac.startDrillConfirm')} pending={start.isPending} onConfirm={() => start.mutate('drill')} />
      </div>
    </Card>
  );
}

function ActiveList({ view, zone, onSignedOut }: { view: SrEvacuationView; zone: string; onSignedOut: () => void }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('unaccounted');
  const [search, setSearch] = useState('');
  const { header, rows } = view;
  const totals = useMemo(() => totalsOf(rows), [rows]);

  const mark = useMutation({
    mutationFn: ({ row, status }: { row: SrRollCallRow; status: RollCallStatus }) =>
      api.post(`${BASE}/evacuations/${header.id}/marks`, {
        body: { rollCallId: row.id, status, idempotencyKey: crypto.randomUUID(), occurredAt: new Date().toISOString() },
        schema: SrMarkResult,
        quiet401: true,
      }),
    onMutate: ({ row, status }) => {
      const prev = qc.getQueryData<SrEvacuationView | null>(keys.active);
      qc.setQueryData<SrEvacuationView | null>(keys.active, (cur) => (cur ? upsertRow(cur, { ...row, status, statusAt: new Date().toISOString() }) : cur));
      return { prev };
    },
    onSuccess: (res) => {
      if (res.row) {
        const row = res.row;
        qc.setQueryData<SrEvacuationView | null>(keys.active, (cur) => (cur ? upsertRow(cur, row) : cur));
      }
    },
    // The optimistic change is wrong if the call failed (or was refused): fetch the truth again.
    onError: (e, _v, ctx) => {
      if (isSignedOut(e)) {
        // Signed out: nothing can be fetched, so put the last list back as it was before the refused mark.
        if (ctx?.prev !== undefined) qc.setQueryData(keys.active, ctx.prev);
        onSignedOut();
      } else void qc.invalidateQueries({ queryKey: keys.active });
    },
  });
  const end = useMutation({
    mutationFn: () => api.post(`${BASE}/evacuations/${header.id}/end`, { schema: SrEvacuationHeader, quiet401: true }),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: keys.active }), qc.invalidateQueries({ queryKey: keys.history })]);
    },
    onError: (e) => {
      if (isSignedOut(e)) onSignedOut();
    },
  });

  const needle = search.trim().toLowerCase();
  const shown = rows.filter((r) => (filter === 'all' || r.status === filter) && (!needle || `${r.displayName} ${r.companyLabel}`.toLowerCase().includes(needle)));
  const groups = (() => {
    const map = new Map<string, { label: string; rows: SrRollCallRow[] }>();
    for (const r of shown) {
      const g = map.get(r.companyLabel) ?? { label: r.companyLabel, rows: [] };
      g.rows.push(r);
      map.set(r.companyLabel, g);
    }
    return [...map.values()]
      .map((g) => ({ ...g, rows: g.rows.sort((a, b) => a.displayName.localeCompare(b.displayName, 'en-GB')) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'en-GB'));
  })();

  const count = (f: Filter): number => (f === 'all' ? rows.length : f === 'off_site' ? totals.offSite : totals[f]);

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-red-300 bg-red-50 p-4" aria-label={t('srb.evac.status')}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={header.kind === 'real' ? 'bad' : 'warn'}>{t(`srb.evac.kind.${header.kind}`)}</Badge>
          <p className="text-sm text-red-950">{t('srb.evac.startedBy', { time: formatDateTime(header.startedAt, zone), by: header.startedLabel })}</p>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" aria-live="polite">
          {(
            [
              ['unaccounted', totals.unaccounted],
              ['safe', totals.safe],
              ['off_site', totals.offSite],
              ['missing', totals.missing],
            ] as const
          ).map(([k, n]) => (
            <div key={k} className="rounded-lg bg-white p-3 text-center shadow-sm">
              <dd className="text-3xl font-bold text-slate-900">{n}</dd>
              <dt className="text-xs text-slate-700">{t(`srb.evac.status.${k}`)}</dt>
            </div>
          ))}
        </dl>
        <div className="mt-3">
          <ConfirmButton label={t('srb.evac.end')} confirmLabel={t('srb.evac.endConfirm')} pending={end.isPending} onConfirm={() => end.mutate()} />
        </div>
        <ErrorLine error={end.error} />
      </section>

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label={t('common.search')} hint={t('srb.evac.searchHint')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div role="group" aria-label={t('srb.evac.filter')} className="flex flex-wrap items-end gap-2">
          {FILTERS.map((f) => (
            <Button key={f} variant={filter === f ? 'primary' : 'secondary'} aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {t(`srb.evac.filter.${f}`)} ({count(f)})
            </Button>
          ))}
        </div>
      </div>

      <ErrorLine error={mark.error} />

      {rows.length === 0 ? (
        <Card>
          <EmptyState message={t('srb.evac.noPeople')} />
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState message={t('srb.evac.noMatch')} />
        </Card>
      ) : (
        groups.map((g) => (
          <section key={g.label} className="rounded-xl border border-slate-200 bg-white shadow-sm" aria-label={g.label}>
            <h2 className="border-b border-slate-100 px-4 py-3 text-base font-semibold text-slate-900">
              {g.label} <span className="text-sm font-normal text-slate-700">({g.rows.length})</span>
            </h2>
            <ul className="divide-y divide-slate-100">
              {g.rows.map((r) => (
                <li key={r.id} className="space-y-2 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-base font-semibold text-slate-900">{r.displayName}</span>
                    <Badge tone={FLAG[r.status]}>{t(`srb.evac.status.${r.status}`)}</Badge>
                    {r.check ? <Badge tone="warn">{t('srb.evac.check')}</Badge> : null}
                  </div>
                  <p className="text-xs text-slate-700">
                    {[r.groupLabel, r.signedInAt ? t('srb.evac.signedIn', { time: formatDateTime(r.signedInAt, zone) }) : null, r.musterPointName ? t('srb.evac.muster', { name: r.musterPointName }) : null]
                      .filter(Boolean)
                      .join(' - ')}
                  </p>
                  <div className="flex flex-wrap gap-2" role="group" aria-label={t('srb.evac.markFor', { name: r.displayName })}>
                    {(['safe', 'off_site', 'missing'] as const).map((s) => (
                      <Button
                        key={s}
                        variant={s === 'safe' ? 'primary' : 'secondary'}
                        className="min-h-11 flex-1 sm:flex-none"
                        disabled={r.status === s || mark.isPending}
                        aria-label={t('srb.evac.markAs', { name: r.displayName, status: t(`srb.evac.mark.${s}`) })}
                        onClick={() => mark.mutate({ row: r, status: s })}
                      >
                        {t(`srb.evac.mark.${s}`)}
                      </Button>
                    ))}
                    {r.status !== 'unaccounted' ? (
                      <Button
                        variant="ghost"
                        className="min-h-11"
                        disabled={mark.isPending}
                        aria-label={t('srb.evac.undoFor', { name: r.displayName })}
                        onClick={() => mark.mutate({ row: r, status: 'unaccounted' })}
                      >
                        {t('srb.evac.undo')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
